import opentype from 'opentype.js';

import type { FontStyle, FontWeight } from '@gelatoas/design-editor-fonts';

export interface ParsedFontInfo {
  name: string;
  family: string;
  fontFile: string;
  weight: FontWeight;
  style: FontStyle;
  isValid: boolean;
  error?: string;
}

function mapWeightClass(usWeightClass: number | undefined): FontWeight {
  if (!usWeightClass) {
    return 'Regular';
  }
  if (usWeightClass <= 100) {
    return 'Thin';
  }
  if (usWeightClass <= 200) {
    return 'ExtraLight';
  }
  if (usWeightClass <= 300) {
    return 'Light';
  }
  if (usWeightClass <= 400) {
    return 'Regular';
  }
  if (usWeightClass <= 500) {
    return 'Medium';
  }
  if (usWeightClass <= 600) {
    return 'SemiBold';
  }
  if (usWeightClass <= 700) {
    return 'Bold';
  }
  if (usWeightClass <= 800) {
    return 'ExtraBold';
  }
  return 'Black';
}

function sanitizeFontFile(filename: string): string {
  const withoutExtension = filename.replace(/\.(ttf|otf)$/i, '');
  return `custom-${withoutExtension.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

function parseFontFile(arrayBuffer: ArrayBuffer, filename: string): ParsedFontInfo {
  try {
    const font = opentype.parse(arrayBuffer);
    const { names } = font;
    const os2 = font.tables?.os2;

    const family =
      names?.fontFamily?.en || (names as any)?.preferredFamily?.en || filename.replace(/\.(ttf|otf)$/i, '');
    const name = names?.fullName?.en || family;
    // eslint-disable-next-line no-bitwise
    const isItalic = os2?.fsSelection ? (os2.fsSelection & 1) !== 0 : false;

    return {
      name,
      family,
      fontFile: sanitizeFontFile(filename),
      weight: mapWeightClass(os2?.usWeightClass),
      style: isItalic ? 'Italic' : 'Normal',
      isValid: true,
    };
  } catch (err) {
    return {
      name: filename,
      family: filename,
      fontFile: sanitizeFontFile(filename),
      weight: 'Regular',
      style: 'Normal',
      isValid: false,
      error: err instanceof Error ? err.message : 'Failed to parse font file',
    };
  }
}

export default parseFontFile;
